/**
 * Bridge write tracking for the drain receipt (S-06, migration plan §6.2 step 4,
 * contract with the MAUI importer: "no writes after the receipt").
 *
 * The receipt says "the handoff on disk holds everything the bridge still
 * owes". Any later write to a drained store (the safety outbox, a Dexie table
 * the drain reads, the location plugin's database) makes that untrue, so every
 * such write:
 *   - bumps a counter (synchronously, inside the write's own call), which the
 *     drain engine compares before writing a receipt: a cycle that saw a write
 *     writes NO receipt and asks to run again;
 *   - removes the receipt (Capacitor Preferences) right away. A safety-outbox
 *     write AWAITS that removal before it writes (write-ahead,
 *     invalidateReceiptNow); a Dexie hook cannot await, so it starts it.
 *
 * The hooks are installed when the database is constructed (db.ts), before any
 * write path can run, on EVERY table: a drained table counts every create,
 * update and delete; any other table counts a write that leaves a row the
 * bridge still owes (a sync flag that is not strictly synced, or pending
 * changes). The engine refuses a receipt while the hooks are not installed.
 *
 * The counter lives in memory: a process restart loses it, which is safe
 * because the receipt is removed at the start of every cycle and only written
 * at the end of one that saw no write.
 *
 * Deliberately free of the BridgeDrain native wrapper (it is loaded eagerly
 * with the database; bridgeDrainInert.contract.spec.ts).
 */
import { Capacitor } from '@capacitor/core';
import { DRAIN_EXEMPT_TABLES, DRAIN_TABLES, isPendingRow, isUndrainedPendingRow } from './drain-sources';

/** The drain receipt's Capacitor Preferences key (read by the MAUI importer). */
export const RECEIPT_KEY = 'cb_drain_receipt_v1';

let writes = 0;
let listener: (() => void) | null = null;
let installed = false;
let remover: (() => Promise<void>) | null = null;

/** The default receipt removal: Capacitor Preferences on the Android shell, nothing elsewhere. */
async function defaultRemove(): Promise<void> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return;
  const { Preferences } = await import('@capacitor/preferences');
  await Preferences.remove({ key: RECEIPT_KEY });
}

function removeReceipt(): Promise<void> {
  return (remover ?? defaultRemove)();
}

/** Record one bridge write and start removing the receipt. Never throws. */
export function noteBridgeWrite(): void {
  writes += 1;
  try {
    listener?.();
  } catch {
    /* the counter already guards the receipt */
  }
  void removeReceipt().catch(() => undefined);
}

/**
 * Write-ahead invalidation for a write that can wait (the safety outbox):
 * bumps the counter and resolves once the receipt is gone. Rejects when it
 * could not be removed; the caller still writes (an SOS is never held back),
 * and the counter keeps any cycle in flight from writing a receipt.
 */
export async function invalidateReceiptNow(): Promise<void> {
  writes += 1;
  try {
    listener?.();
  } catch {
    /* see noteBridgeWrite */
  }
  await removeReceipt();
}

/** How many bridge writes this process has seen. Only ever increases. */
export function bridgeWriteCount(): number {
  return writes;
}

/** True once the hooks are on the database: the engine writes no receipt before that. */
export function bridgeWriteTrackerInstalled(): boolean {
  return installed;
}

/** Called on every bridge write (tests, diagnostics). */
export function setBridgeWriteListener(fn: (() => void) | null): void {
  listener = fn;
}

/** How the receipt is removed (the runtime passes its Preferences; tests a fake). */
export function setReceiptRemover(fn: (() => Promise<void>) | null): void {
  remover = fn;
}

/** A Dexie-like table with the creating/updating/deleting hooks. */
interface HookableTable {
  name?: string;
  hook(event: 'creating' | 'updating' | 'deleting', fn: (...args: unknown[]) => unknown): unknown;
}

const hooked = new WeakSet<object>();

/** Who is writing now (client-id.ts registers currentOriginator); all null until then. */
export interface WriterOrigin {
  userId: string | null;
  tenantId: string | null;
  deviceSessionId: string | null;
}
let originProvider: (() => WriterOrigin) | null = null;

/** Registered by client-id.ts at load, so every unsynced write can be stamped with its originator. */
export function setOriginProvider(fn: (() => WriterOrigin) | null): void {
  originProvider = fn;
}

/** How a table's row counts as "still owed by the bridge" (the stamping rule follows it). */
type PendingRule = (row: Record<string, unknown>) => boolean;

function pendingRuleFor(name: string, drained: boolean): PendingRule {
  const spec = DRAIN_TABLES.find((t) => t.table === name);
  if (spec?.mode === 'queue') return () => true;
  if (spec?.mode === 'syncedAt') return (row) => row.synced_at === undefined || row.synced_at === null || row.synced_at === '';
  if (spec?.mode === 'flag') return (row) => isPendingRow(row as { is_synced?: unknown }) || row.pending_changes === true;
  return drained ? () => false : (row) => isUndrainedPendingRow(row);
}

/**
 * Does this stored row still hold work the bridge owes the server (the same rule the receipt
 * guard and the stamping hooks use)? A row that does not is a cache / server copy.
 */
export function isOwedRow(tableName: string, row: Record<string, unknown>): boolean {
  const drained = DRAIN_TABLES.some((t) => t.table === tableName) || DRAIN_EXEMPT_TABLES.has(tableName);
  return pendingRuleFor(tableName, drained)(row);
}

/** Who a stored row's pending work belongs to: its originator stamp (a form draft names its author as user_id). */
export function originatorOfRow(tableName: string, row: Record<string, unknown>): string | null {
  if (typeof row.origin_user_id === 'string' && row.origin_user_id) return row.origin_user_id;
  const spec = DRAIN_TABLES.find((t) => t.table === tableName);
  return spec?.draft && typeof row.user_id === 'string' && row.user_id ? row.user_id : null;
}

/**
 * The originator fields for a row that is (becoming) owed and has no stamp yet:
 * the user signed in now, the row's own tenant (else the active one), the
 * device session. Null when nobody is signed in (an older build's behaviour).
 */
function originFor(row: Record<string, unknown>): Record<string, string> | null {
  if (row.origin_user_id) return null;
  let who: WriterOrigin | null = null;
  try {
    who = originProvider ? originProvider() : null;
  } catch {
    who = null;
  }
  if (!who?.userId) return null;
  const tenant = (typeof row.tenant_id === 'string' && row.tenant_id) || who.tenantId;
  const out: Record<string, string> = { origin_user_id: who.userId };
  if (!row.origin_tenant_id && tenant) out.origin_tenant_id = tenant;
  if (!row.origin_device_session_id && who.deviceSessionId) out.origin_device_session_id = who.deviceSessionId;
  return out;
}

/**
 * The originator stamp of a row that became owed while nobody was signed in (an expired
 * session's sync bookkeeping, a background writer). Nobody can vouch for who wrote it, so
 * every sync path defers it (client-id.ts belongsToAnotherUser) and the drain hands it off
 * with no user (drain-engine.ts), which the server quarantines as ORIGIN_INCOMPLETE.
 */
export const UNKNOWN_ORIGINATOR = 'unknown-originator';

/**
 * Marks an update as sync bookkeeping, not a person's edit: e.g. a sync that marked a row
 * synced and then puts it back to pending because a follow-up call failed. The hooks leave
 * the row's originator stamp exactly as it is (whoever is signed in by then did not write
 * it), and remove the marker itself before the row is stored.
 */
export const SYNC_BOOKKEEPING_KEY = '__cb_sync_bookkeeping';

export function syncBookkeeping<T extends object>(mods: T): T {
  return { ...mods, [SYNC_BOOKKEEPING_KEY]: true };
}

function currentWriter(): WriterOrigin | null {
  try {
    return originProvider ? originProvider() : null;
  } catch {
    return null;
  }
}

/**
 * The originator of a row that BECOMES owed now (it was synced, or nothing was owed), or
 * null to leave the stamp as it is.
 *  - Someone signed in: whoever writes it now, overriding any stamp left from an earlier,
 *    synced, version (a stamp left by the person who first created it must never carry
 *    someone else's later offline edit).
 *  - Nobody signed in: that write is not a person's (sync bookkeeping, e.g. an expired
 *    session's revert): an existing stamp is NEVER cleared or changed; a row with no stamp
 *    gets UNKNOWN_ORIGINATOR, never left unstamped (an unstamped row is everyone's).
 *  - No provider registered yet (client-id.ts not loaded): nothing is known, nothing changes.
 */
function restampFor(row: Record<string, unknown>): Record<string, string | null> | null {
  if (!originProvider) return null;
  const who = currentWriter();
  const tenant = (typeof row.tenant_id === 'string' && row.tenant_id) || who?.tenantId || null;
  if (!who?.userId) {
    if (row.origin_user_id) return null;
    return {
      origin_user_id: UNKNOWN_ORIGINATOR,
      origin_tenant_id: (typeof row.origin_tenant_id === 'string' && row.origin_tenant_id) || tenant,
      origin_device_session_id: null,
    };
  }
  return { origin_user_id: who.userId, origin_tenant_id: tenant, origin_device_session_id: who.deviceSessionId ?? null };
}

/**
 * A different signed-in user wrote to a row whose pending change another originator owns.
 * Accepted limitation (bridge-drain README): the first writer of a pending change owns it, so
 * this edit goes out under that writer. Logged (no ids, no data) so it is observable.
 */
function warnIfForeignEdit(table: string, before: Record<string, unknown>): void {
  const owner = before.origin_user_id;
  if (!owner || typeof owner !== 'string') return;
  const who = currentWriter();
  if (!who?.userId || who.userId === owner) return;
  // eslint-disable-next-line no-console
  console.warn(`[drain] ${table}: a signed-in user edited a pending change owned by another originator; it will be sent under the first writer.`);
}

function hookTable(table: HookableTable, drained: boolean): boolean {
  // Keyed by the hook emitter, not the Table object: Dexie hands out different Table objects for
  // one store (db[name] vs db.tables) that share one emitter, and a second subscription would
  // run every hook twice (the second one seeing the first one's changes).
  if (!table || typeof table.hook !== 'function' || hooked.has(table.hook)) return false;
  const name = table.name ?? '';
  // The safety outbox stamps its entries from the event itself (safety-outbox.service.ts).
  const stamps = !DRAIN_EXEMPT_TABLES.has(name);
  const owed = pendingRuleFor(name, drained);
  table.hook('creating', (_key: unknown, obj: unknown) => {
    const row = (obj as Record<string, unknown>) ?? {};
    const bookkeeping = Object.prototype.hasOwnProperty.call(row, SYNC_BOOKKEEPING_KEY);
    if (bookkeeping) delete row[SYNC_BOOKKEEPING_KEY];
    const isOwed = owed(row);
    // Every unsynced write is stamped with who made it, at the moment it is made (S-06 M6):
    // the sync paths then send it only for that person, and the handoff carries its origin.
    // An existing stamp (a row re-put by a sync) is kept; sync bookkeeping never stamps.
    if (stamps && isOwed && !bookkeeping && !row.origin_user_id) {
      const origin = originFor(row) ?? restampFor(row);
      if (origin) Object.assign(row, origin);
    }
    if (drained || isOwed) noteBridgeWrite();
  });
  table.hook('updating', (mods: unknown, _key: unknown, obj: unknown) => {
    const before = (obj as Record<string, unknown>) ?? {};
    const changes = (mods as Record<string, unknown>) ?? {};
    // By presence: a hook returning { key: undefined } is how the marker is removed.
    const bookkeeping = Object.prototype.hasOwnProperty.call(changes, SYNC_BOOKKEEPING_KEY);
    const next: Record<string, unknown> = { ...before, ...changes };
    delete next[SYNC_BOOKKEEPING_KEY];
    const isOwed = owed(next);
    if (drained || isOwed) noteBridgeWrite();
    // Sync bookkeeping (syncBookkeeping()): the originator stamp stays exactly as it is.
    if (bookkeeping) return { [SYNC_BOOKKEEPING_KEY]: undefined };
    if (stamps && isOwed) {
      // Not owed before (synced, or a cache row) and owed now: this edit is the writer's own
      // (or, with nobody signed in, nobody's: see restampFor).
      if (!owed(before)) return restampFor(next) ?? undefined;
      warnIfForeignEdit(name, before);
      const origin = originFor(next);
      if (origin) return origin;
    }
    return undefined;
  });
  if (drained) {
    table.hook('deleting', () => {
      noteBridgeWrite();
    });
  }
  hooked.add(table.hook);
  return true;
}
/**
 * Hook every table of the database (and any named one): idempotent per table
 * object. Returns how many tables were newly hooked.
 */
export function installDrainWriteHooks(
  dbLike: Record<string, unknown> & { tables?: ReadonlyArray<HookableTable> },
  extra: readonly string[] = []
): number {
  const drainedNames = new Set<string>([...DRAIN_TABLES.map((t) => t.table), ...DRAIN_EXEMPT_TABLES, ...extra]);
  let n = 0;
  for (const name of drainedNames) {
    if (hookTable(dbLike[name] as HookableTable, true)) n += 1;
  }
  for (const table of dbLike.tables ?? []) {
    if (hookTable(table, drainedNames.has(table.name ?? ''))) n += 1;
  }
  installed = true;
  return n;
}

/** For tests. */
export function __resetBridgeWritesForTests(): void {
  writes = 0;
  listener = null;
  remover = null;
}
